/**
 *
 * @Copyright 2025 UNLOCKIT DECENTRALIZATION, LDA
 *
 */

import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { toast } from 'react-toastify';
import {
    TranslationContext,
    withTranslationContext,
} from '../../../controllers/TranslationContext';

import { ReactComponent as GeneralConfigsIcon } from '../../../../assets/images/businesses.svg';
import { ButtonVariant } from '../../../../types/general';
import Button from '../../Button';
import { Drawer } from '../../Drawer';
import { FormField } from '../../FormField';
import { Tooltip } from '../../Tooltip';
import { withWorkflowContext, WorkflowContext } from '../../../controllers/WorkflowContext';
import { WorkflowDocument } from '../../../../types/workflows';
import { DatePicker } from '../../DatePicker';

type OwnProps = TranslationContext & Pick<WorkflowContext, 'updateWorkflowDocument'> & {
    document: WorkflowDocument;
    editable?: boolean;
};

const GeneralConfigOptionComponent: FunctionComponent<OwnProps> = (props: OwnProps) => {
    const {
        t,
        document,
        updateWorkflowDocument,
        editable = false,
    } = props;

    const [docEdited, setDocEdited] = useState(document);
    const [isUploading, setIsUploading] = useState(false);
    const [isOpenDrawer, setIsOpenDrawer] = useState(false);

    /**
     * onClose callback function
     *
     * @remarks
     * Close Drawer and resets defaults
     */
    const onClose = () => {
        setDocEdited(document);
        setIsOpenDrawer(false);
    };

    /**
     * Submit Edit Form
     *
     * @remarks
     * Validate and submit fields
     */
    const onFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsUploading(true);
        const [data, error] = await updateWorkflowDocument(String(document.transactionId), String(document.id), {
            documentTypeId: String(document.documentTypeId),
            name: docEdited.name,
            expiresAt: docEdited.expiresAt ?? undefined,
        });
        setIsUploading(false);
        if (data) {
            onSuccess();
        }
        if (error) {
            onFailure(error.errors?.[0]?.getMessageTranslated(t));
        }
    };

    /**
     * onSuccess callback function
     *
     * @remarks
     * Update state (and other tasks if needed) after successful patch response
     */
    const onSuccess = () => {
        setIsOpenDrawer(false);
        toast.success(t('workflows.documents.documentUpdated'));
    };

    /**
     * onFailure callback function
     *
     * @remarks
     * Show toast with generic failure message
     */
    const onFailure = (errorMessage: string) => {
        toast.error(errorMessage);
    };

    const handleExpirationDateChange = (date: Date | null) => {
        if (!date || isNaN(date.getTime())) return;
        setDocEdited({
            ...docEdited,
            expiresAt: date.toISOString(),
        });
    };

    return (
        <>
            <Button
                variant={ButtonVariant.IconBtn}
                extraClasses={classNames('first-config-btn primary',
                    { active: isOpenDrawer })
                }
                onClick={() => setIsOpenDrawer(true)}
                testId="general-configs-btn"
            >
                <Tooltip title={t('workflows.documents.generalSettings')} placement="left">
                    <GeneralConfigsIcon />
                </Tooltip>
            </Button>
            <Drawer
                title={t('workflows.documents.generalSettings')}
                open={isOpenDrawer}
                handleClose={onClose}
            >
                <form
                    className="form"
                    onSubmit={onFormSubmit}
                    autoComplete="off"
                >
                    <div className="form__fields">
                        <div className="form__fields__state">
                            <label>{t('workflows.documents.status')}</label>
                            <p>{t(`workflows.documents.review.status.${document.documentStatus}`)}</p>
                            <p>{document.documentNotes}</p>
                        </div>
                        <FormField
                            name="name"
                            value={docEdited.name}
                            label={t('workflows.documents.name')}
                            placeholder={t('workflows.documents.placeholders.name')}
                            onChange={(_, value) => setDocEdited({ ...docEdited, name: value })}
                            maxLength={250}
                            fullWidth
                            disabled={isUploading || !editable}
                        />
                        <DatePicker
                            name="expiresAt"
                            label={t('workflows.documents.expiresAt')}
                            format="DD/MM/YYYY"
                            value={docEdited.expiresAt ?? ''}
                            onChange={handleExpirationDateChange}
                            extraClasses="full-width"
                            disabled={isUploading || !editable}
                        />
                    </div>
                    <div className="form__bottom">
                        {editable && (
                            <Button
                                isSubmit
                                variant={ButtonVariant.Curved}
                                extraClasses="primary"
                                testId="save-btn"
                                disabled={isUploading}
                            >
                                {t('general.save')}
                            </Button>
                        )}
                    </div>
                </form>
            </Drawer>
        </>
    );
};

export const GeneralConfigOption = withTranslationContext(withWorkflowContext(GeneralConfigOptionComponent));
